import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Menu, X, ChevronDown } from 'lucide-react';

const LEFT_NAV = [
  { label: 'HOME',               href: '/' },
  { label: 'PREVIOUS LECTURES', href: '/lectures' },
  { label: 'NEWS',               href: '/news' },
  { label: 'GALLERY',            href: '/gallery' },
  { label: 'PARTNERSHIPS',       href: '/partnerships' },
];

const ABOUT_DROPDOWN = [
  { label: 'About Us',      href: '/about' },
  { label: 'The Beginning', href: '/about#beginning' },
  { label: 'The Now',       href: '/about#now' },
  { label: 'Our Team',      href: '/about#team' },
  { label: 'Our Partners',  href: '/about#partners' },
];

export default function Navbar() {
  const [scrolled,     setScrolled]     = useState(false);
  const [isMenuOpen,   setIsMenuOpen]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate    = useNavigate();
  const location    = useLocation();
  const onAboutPage = location.pathname === '/about';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  /* Close dropdown when clicking outside */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setDropdownOpen(false);
    setIsMenuOpen(false);

    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      if (path === '' || path === location.pathname) {
        /* Same page — scroll to anchor */
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        /* Different page — navigate then scroll */
        navigate(path);
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 120);
      }
    } else {
      navigate(href);
    }
  };

  const baseLink = (isScrolled) => ({
    color: isScrolled ? '#17140F' : 'rgba(255,255,255,0.92)',
    textDecoration: 'none', fontSize: '0.75rem', fontWeight: '600',
    letterSpacing: '0.05em', padding: '0.45rem 0.65rem', borderRadius: '6px',
    whiteSpace: 'nowrap', transition: 'color 0.25s ease', cursor: 'pointer',
  });

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? '#FFFFFF' : 'transparent',
      transition: 'background 0.5s ease',
      boxShadow: 'none',
      border: 'none',
      outline: 'none',
    }}>
      <div style={{
        maxWidth: '1440px', margin: '0 auto',
        padding: scrolled ? '0.7rem 1.5rem' : '1.1rem 1.5rem',
        display: 'flex', alignItems: 'center', gap: '1rem',
        transition: 'padding 0.5s ease',
      }}>

        {/* Logo */}
        <a href="/" onClick={(e) => go(e, '/')} style={{ flexShrink: 0 }}>
          <img src="/Assets/logo.png" alt="Drunken Lectures" style={{
            height: '42px', width: 'auto',
            filter: scrolled ? 'none' : 'brightness(0) invert(1)',
            transition: 'filter 0.4s ease',
          }} />
        </a>

        {/* Left nav */}
        <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', flex: 1, marginLeft: '1rem' }}>
          {LEFT_NAV.map(item => (
            <a key={item.label} href={item.href}
              {...(!item.external && { onClick: (e) => go(e, item.href) })}
              {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
              style={baseLink(scrolled)}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#E8672E'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = scrolled ? '#17140F' : 'rgba(255,255,255,0.92)'; }}>
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right nav: ABOUT US dropdown + CONTACT US */}
        <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>

          {/* CONTACT US */}
          <a href="/contact" onClick={(e) => go(e, '/contact')}
            style={{ ...baseLink(scrolled), color: scrolled ? '#6B665C' : 'rgba(255,255,255,0.72)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#E8672E'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = scrolled ? '#6B665C' : 'rgba(255,255,255,0.72)'; }}>
            CONTACT US
          </a>

          {/* ── ABOUT US dropdown ── */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setDropdownOpen(o => !o)}
              style={{
                ...baseLink(scrolled),
                background: 'none', border: 'none',
                color: onAboutPage ? '#E8672E' : (scrolled ? '#6B665C' : 'rgba(255,255,255,0.72)'),
                display: 'flex', alignItems: 'center', gap: '0.25rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#E8672E'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = onAboutPage ? '#E8672E' : (scrolled ? '#6B665C' : 'rgba(255,255,255,0.72)'); }}
            >
              ABOUT US
              <ChevronDown size={13} style={{ transition: 'transform 0.25s ease', transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>

            {dropdownOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: '#1C3A56', borderRadius: '12px', minWidth: '190px',
                boxShadow: '0 16px 48px rgba(0,0,0,0.28)', overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                animation: 'dropFade 0.18s ease',
              }}>
                {ABOUT_DROPDOWN.map((item, i) => (
                  <a key={item.label} href={item.href} onClick={(e) => go(e, item.href)}
                    style={{
                      display: 'block', padding: '0.75rem 1.2rem',
                      color: i === 0 ? '#E8672E' : 'rgba(255,255,255,0.8)',
                      textDecoration: 'none', fontSize: '0.85rem', fontWeight: i === 0 ? '700' : '500',
                      letterSpacing: '0.02em', borderBottom: i < ABOUT_DROPDOWN.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                      transition: 'background 0.15s ease, color 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(232,103,46,0.12)'; e.currentTarget.style.color = '#E8672E'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = i === 0 ? '#E8672E' : 'rgba(255,255,255,0.8)'; }}>
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* SHOP */}
          <a href="/merch" onClick={(e) => go(e, '/merch')}
            style={{ ...baseLink(scrolled), color: scrolled ? '#6B665C' : 'rgba(255,255,255,0.72)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#E8672E'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = scrolled ? '#6B665C' : 'rgba(255,255,255,0.72)'; }}>
            SHOP
          </a>

          {/* Divider */}
          <div style={{ width: '1px', height: '20px', background: scrolled ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.25)', margin: '0 0.5rem' }} />

          {/* User — links to merch waitlist until online store launches */}
          <a href="/merch" onClick={(e) => go(e, '/merch')}
            style={{ padding: '0.5rem 0.4rem', color: scrolled ? '#17140F' : 'white', display: 'flex', alignItems: 'center' }}
            aria-label="Shop merch">
            <User size={20} />
          </a>

        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="mobile-menu-btn"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: scrolled ? '1px solid rgba(26,26,26,0.2)' : '1px solid rgba(255,255,255,0.35)',
            borderRadius: '10px',
            cursor: 'pointer',
            padding: '0.55rem',
            color: scrolled ? '#17140F' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '44px',
            minHeight: '44px',
            transition: 'border-color 0.25s ease, background 0.25s ease',
          }}>
          {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer — outer div handles clip/animation, inner div has the border.
          Keeping border on the outer div caused it to render as a 1px black line on
          desktop (maxHeight:0 collapses content but overflow:hidden doesn't clip
          the element's own border). */}
      <div style={{
        overflow: 'hidden',
        maxHeight: isMenuOpen ? '100vh' : '0',
        transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)',
      }}>
      <div style={{
        background: '#1C3A56',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ padding: '0.5rem 1.5rem 2.5rem' }}>
          {[...LEFT_NAV, ...ABOUT_DROPDOWN.map(i => ({ ...i, indent: true })), { label: 'CONTACT US', href: '/contact' }, { label: 'SHOP', href: '/merch' }].map((item, i) => (
            <a
              key={`${item.label}-${i}`}
              href={item.href}
              onClick={item.external ? undefined : (e) => go(e, item.href)}
              {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                color: item.label === 'About Us' ? '#E8672E' : 'rgba(255,255,255,0.85)',
                textDecoration: 'none', fontWeight: '600',
                fontSize: item.indent ? '0.88rem' : '1rem',
                padding: item.indent ? '0.7rem 0 0.7rem 1.25rem' : '0.9rem 0',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                letterSpacing: '0.03em',
                transition: 'color 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#E8672E'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = item.label === 'About Us' ? '#E8672E' : 'rgba(255,255,255,0.85)'; }}>
              {item.indent ? `— ${item.label}` : item.label}
            </a>
          ))}
        </div>
      </div>
      </div>

      <style>{`
        @keyframes dropFade { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @media (min-width: 900px) { .desktop-nav { display: flex !important; } .mobile-menu-btn { display: none !important; } }
        @media (max-width: 899px) { .desktop-nav { display: none !important; } .mobile-menu-btn { display: flex !important; } }
        a:focus-visible, button:focus-visible { outline: 2px solid #E8672E; outline-offset: 2px; }
        * { box-sizing: border-box; } body { margin: 0; padding: 0; }
      `}</style>
    </header>
  );
}
